import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Store } from "redux";
import { useTranslation } from "components/translations/useTranslation";
import { Button, ButtonType } from "components/common/Button.component";
import { FormField } from "components/common/FormField.component";
import { Notification, NotificationType } from "components/common/Notification.component";
import Checkbox from "components/common/checkbox.component";
import UserSearch from "components/input/UserSearch.component";
import DateFilter from "components/input/CalendarInput.component";
import { NewCalendar } from "components/common/calendar/Calendar.component";
import { DateRange } from "components/common/calendar/CalendarQuickSelect.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { launchDarkly } from "utils/launchDarkly";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import * as reduxSelectors from "components/store/application.reducers";
import {
    AddSubstituteProps,
    SubstituteActionData,
} from "types/modals";
import {
    saveSubstitute as saveSubstituteAction,
    getSubstituteForUser,
    validateSubstitute as validateSubstituteAction,
} from "components/modals/modal.actions";

import "components/popup/AddSubstitutePopup.scss";

const FROM = "from";
const TO = "to";

interface UserOption {
    value: string | number | undefined;
    label: string | undefined;
}

const getToday = () => moment().format("YYYY-MM-DD");

const formatDateValue = (val: any) => {
    const datedValue = moment(val);
    return (datedValue.isValid() ? datedValue : moment()).format("YYYY-MM-DD");
};

// Dates travel through this modal as plain "YYYY-MM-DD" strings, which the Date constructor
// parses as UTC midnight and so shifts the day for negative UTC offsets. Everything therefore
// goes through moment, which parses them as local midnight, pinned to midday so the day never
// moves across a DST boundary either.
const toLocalMidday = (val: any) => moment(val).hours(12).minutes(0).seconds(0).milliseconds(0);

const toIsoDay = (val: any) => toLocalMidday(val).format("YYYY-MM-DD");

const AddSubstitute = ({ more, closeModal, loopFocus, errorCallback }: AddSubstituteProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const edit = more?.edit;
    const userLanguage = useSelector((store: Store) => reduxSelectors.getLanguage(store));
    const dateFormat = useSelector((store: Store) => reduxSelectors.getDateFormatForLanguage(store));
    const useNewCalendar = launchDarkly.userClient?.variation("fe-use-new-calendar", false) ?? false;

    const emptyUser: UserOption = { value: undefined, label: undefined };

    const [approver, setApprover] = useState<UserOption>(
        edit ? { value: edit.userId, label: edit.userName } : { ...emptyUser },
    );
    const [substitute, setSubstitute] = useState<UserOption>(
        edit ? { value: edit.substituteId, label: edit.substituteName } : { ...emptyUser },
    );
    const [from, setFrom] = useState<string>(edit ? moment(edit.from).format("YYYY-MM-DD") : getToday());
    const [to, setTo] = useState<string | null>(
        edit
            ? (!edit.to && edit.from ? null : moment(edit.to).format("YYYY-MM-DD"))
            : getToday(),
    );
    const [noEndDate, setNoEndDate] = useState<boolean>(!!(edit && !edit.to && edit.from));
    const [version, setVersion] = useState<number>(edit?.version ?? 0);
    const [disableSave, setDisableSave] = useState<boolean>(false);
    const [focusApprover, setFocusApprover] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);

    const fromRef = useRef<any>(null);
    const toRef = useRef<any>(null);

    const buildSubstitute = useCallback(
        (active: boolean = true): SubstituteActionData => {
            const result: SubstituteActionData = {
                from: toIsoDay(from),
                substituteName: substitute.label ?? "",
                userName: approver.label ?? "",
                substituteId: substitute.value as string | number,
                userId: approver.value as string | number,
                active,
                version,
            };
            if (!noEndDate && to) {
                result.to = toIsoDay(to);
            }
            return result;
        },
        [approver, substitute, from, to, noEndDate, version],
    );

    const validate = useCallback(
        (overrides: Partial<{
            approver: UserOption;
            substitute: UserOption;
            from: string;
            to: string | null;
            noEndDate: boolean;
            version: number;
        }> = {}) => {
            const nextApprover = overrides.approver ?? approver;
            const nextSubstitute = overrides.substitute ?? substitute;
            if (nextSubstitute.value === undefined || nextApprover.value === undefined) {
                return;
            }
            const nextFrom = overrides.from ?? from;
            const nextTo = overrides.to !== undefined ? overrides.to : to;
            const nextNoEnd = overrides.noEndDate ?? noEndDate;
            const nextVersion = overrides.version ?? version;

            const payload: SubstituteActionData = {
                from: toIsoDay(nextFrom),
                substituteName: nextSubstitute.label ?? "",
                userName: nextApprover.label ?? "",
                substituteId: nextSubstitute.value as string | number,
                userId: nextApprover.value as string | number,
                active: true,
                version: nextVersion,
            };
            if (!nextNoEnd && nextTo) {
                payload.to = toIsoDay(nextTo);
            }

            dispatch(
                validateSubstituteAction(
                    payload,
                    (response: any) => {
                        setDisableSave(false);
                        setErrorMessage(null);
                        setWarningMessage(response?.substituteMessage?.message ?? null);
                    },
                    (err: any) => {
                        setDisableSave(true);
                        setErrorMessage(err?.error?.errorMessages ?? null);
                    },
                ),
            );
        },
        [approver, substitute, from, to, noEndDate, version, dispatch],
    );

    const handleApproverChange = useCallback(
        (value: UserOption) => {
            setApprover(value);
            setFocusApprover(false);

            if (value?.value === undefined) {
                setSubstitute({ value: undefined, label: undefined });
                setFrom(getToday());
                setTo(getToday());
                setNoEndDate(false);
                setVersion(0);
                setWarningMessage(null);
                setErrorMessage(null);
                setDisableSave(true);
                return;
            }

            dispatch(
                getSubstituteForUser(
                    value.value,
                    (response: any) => {
                        if (response.substituteId !== 0) {
                            const mappedFrom =
                                moment(response.from).valueOf() < moment().valueOf()
                                    ? moment().format("YYYY-MM-DD")
                                    : moment(response.from).format("YYYY-MM-DD");
                            const mappedTo = response.to
                                ? moment(response.to).valueOf() < moment().valueOf()
                                    ? moment().format("YYYY-MM-DD")
                                    : moment(response.to).format("YYYY-MM-DD")
                                : null;
                            const mappedSubstitute: UserOption = {
                                value: response.substituteId,
                                label: response.substituteName,
                            };
                            const hasNoEnd = !response.to && response.from !== null;
                            const nextWarning = response.substituteMessage?.message ?? null;

                            setSubstitute(mappedSubstitute);
                            setFrom(mappedFrom);
                            setTo(mappedTo);
                            setNoEndDate(hasNoEnd);
                            setVersion(response.version);
                            setWarningMessage(nextWarning);

                            validate({
                                approver: value,
                                substitute: mappedSubstitute,
                                from: mappedFrom,
                                to: mappedTo,
                                noEndDate: hasNoEnd,
                                version: response.version,
                            });
                        } else {
                            setSubstitute({ value: undefined, label: "" });
                            setErrorMessage(null);
                            setDisableSave(true);
                        }
                    },
                    (err: any) => {
                        errorCallback?.(err);
                    },
                ),
            );
        },
        [dispatch, errorCallback, validate],
    );

    const handleSubstituteChange = useCallback(
        (value: UserOption) => {
            setSubstitute(value);
            setWarningMessage(null);
            validate({ substitute: value });
        },
        [validate],
    );

    const changeDate = useCallback(
        (val: any, type: typeof FROM | typeof TO) => {
            const fromDate = toLocalMidday(type === FROM ? val : fromRef.current?.getValue?.() ?? from);
            let toDate: ReturnType<typeof toLocalMidday> | null = type === TO ? toLocalMidday(val) : null;
            if (type !== TO && !noEndDate) {
                // The new calendar exposes no imperative value, so the current "to" comes from
                // state when the legacy picker's ref is not there.
                const currentTo = toRef.current?.getValue?.() ?? to;
                if (currentTo) {
                    toDate = toLocalMidday(currentTo);
                }
            }

            const formattedValue = formatDateValue(val);

            let nextFrom = from;
            let nextTo = to;

            if (toDate && fromDate.valueOf() > toDate.valueOf()) {
                if (type === TO) {
                    nextFrom = formattedValue;
                } else {
                    nextTo = formattedValue;
                }
            }

            if (type === FROM) {
                nextFrom = formattedValue;
            } else {
                nextTo = formattedValue;
            }

            setFrom(nextFrom);
            setTo(nextTo);
            validate({ from: nextFrom, to: nextTo });
        },
        [from, to, noEndDate, validate],
    );

    const handleNoEndDate = useCallback(
        (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLLabelElement>) => {
            const target = (event as any).target as HTMLInputElement;
            const newChecked = target?.type === "checkbox" ? target.checked : !noEndDate;

            let toValue: string | null = null;
            if (!newChecked) {
                toValue = toLocalMidday(from).add(1, "day").format("YYYY-MM-DD");
            }

            setTo(toValue);
            setNoEndDate(newChecked);
            validate({ to: toValue, noEndDate: newChecked });
        },
        [from, noEndDate, validate],
    );

    const handleSaveSuccess = useCallback(() => {
        if (edit) {
            closeModal();
        } else {
            more?.saveCloseCallback?.();
            closeModal();
        }
    }, [edit, more, closeModal]);

    const handleSaveError = useCallback((err: any) => {
        setErrorMessage(err?.error?.errorMessages ?? null);
    }, []);

    const doSave = useCallback(
        (active: boolean) => {
            const payload = buildSubstitute(active);
            dispatch(saveSubstituteAction(payload, handleSaveSuccess, handleSaveError));
        },
        [buildSubstitute, dispatch, handleSaveSuccess, handleSaveError],
    );

    const handleOk = useCallback(() => doSave(true), [doSave]);
    const deactivate = useCallback(() => doSave(false), [doSave]);

    useEffect(() => {
        if (!edit) {
            setFocusApprover(true);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount only

    const isSaveEnabled = !(disableSave || substitute.value === undefined || approver.value === undefined);
    const approverDisabled = approver.value === undefined;
    const fromSelectedValue = moment(from).valueOf() < moment().valueOf() ? moment() : from;
    const toSelectedValue = noEndDate
        ? moment(null as any)
        : moment(to).valueOf() < moment().valueOf()
            ? moment()
            : to;

    const renderDatePicker = (
        type: typeof FROM | typeof TO,
        selectedValue: any,
        ref: React.MutableRefObject<any>,
        tabIndex: number,
    ) => {
        if (useNewCalendar) {
            const selectedDate = moment(selectedValue).toDate();
            return (
                <NewCalendar
                    // The calendar only reads the initial dates on mount, so it is remounted
                    // whenever the stored date changes.
                    key={`${type}-${moment(selectedValue).format("YYYY-MM-DD")}`}
                    id={`substitute-${type}-datepicker`}
                    singlePanel
                    translate={translate}
                    disablePastDates
                    disabled={approverDisabled}
                    tabIndex={tabIndex}
                    initialStartDate={selectedDate}
                    initialEndDate={selectedDate}
                    onChange={(range: DateRange) => {
                        if (range.startDate) {
                            changeDate(range.startDate, type);
                        }
                    }}
                />
            );
        }

        return (
            <DateFilter
                ref={ref}
                style="popup"
                translate={translate}
                userLanguage={userLanguage}
                format={dateFormat}
                minDate={moment()}
                tabIndex={tabIndex}
                isDisabled={approverDisabled}
                selectedValue={selectedValue}
                onChange={(value: any) => changeDate(value, type)}
            />
        );
    };

    return (
        <>
            <div className="ga-modal__content" id="add_substitute_pop_up">
                <FormField id="approver-search" label={translate("popUp.addSubstitute.approver")}>
                    <UserSearch
                        includeSameUser={true}
                        refresh={focusApprover}
                        translate={translate as any}
                        tabindex={MODAL_CONTENT_TAB_INDEX}
                        placeholder={translate("popUp.addSubstitute.name")}
                        propagateValue={handleApproverChange}
                        value={approver}
                        menuPosition={"fixed"}
                        menuPortalTarget={document.body}
                    />
                </FormField>
                <FormField
                    id="substitute-search"
                    label={translate("popUp.addSubstitute.substitute")}
                    disabled={approverDisabled}
                >
                    <UserSearch
                        includeSameUser={true}
                        tabindex={MODAL_CONTENT_TAB_INDEX + 1}
                        placeholder={translate("popUp.addSubstitute.name")}
                        propagateValue={handleSubstituteChange}
                        translate={translate as any}
                        value={substitute}
                        menuPosition={"fixed"}
                        menuPortalTarget={document.body}
                    />
                </FormField>

                {warningMessage && (
                    <div className="my-3">
                        <Notification type={NotificationType.WARNING} description={warningMessage} />
                    </div>
                )}
                {errorMessage && (
                    <div className="my-3">
                        <Notification type={NotificationType.ERROR} description={errorMessage} />
                    </div>
                )}

                <div className="row mt-4 mx-0 align-items-end">
                    <div className="col-md-4 px-0">
                        <div>{translate("settings.substitute.from")}</div>
                        <div>
                            {renderDatePicker(FROM, fromSelectedValue, fromRef, MODAL_CONTENT_TAB_INDEX + 2)}
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div>{translate("settings.substitute.to")}</div>
                        {noEndDate ? (
                            <span>{translate("substitutes.indefinitely")}</span>
                        ) : (
                            <div>
                                {renderDatePicker(TO, toSelectedValue, toRef, MODAL_CONTENT_TAB_INDEX + 3)}
                            </div>
                        )}
                    </div>

                    <div className="col-md-4">
                        <Checkbox
                            inputId="noEndDate"
                            isChecked={noEndDate}
                            isDisabled={approverDisabled}
                            tabIndex={MODAL_CONTENT_TAB_INDEX + 4}
                            onChange={handleNoEndDate}
                            label={translate("popUp.addSubstitute.noEndDate")}
                        />
                    </div>
                </div>
            </div>

            {edit ? (
                <div className="ga-modal__actions">
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={deactivate}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 5}
                        text={translate("popUp.editSubstitute.stopNow")}
                    />
                    <Button
                        type={ButtonType.PRIMARY}
                        onClick={handleOk}
                        disabled={!isSaveEnabled}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 6}
                        text={translate("popUp.ok")}
                    />
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={closeModal}
                        onBlur={loopFocus}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 7}
                        text={translate("popUp.cancel")}
                    />
                </div>
            ) : (
                <OKCancelButtons
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 5}
                    okEnabled={isSaveEnabled}
                    okCallback={handleOk}
                    okText={translate("popUp.ok")}
                    cancelEnabled={true}
                    cancelCallback={closeModal}
                    cancelText={translate("popUp.cancel")}
                    loopFocus={loopFocus}
                />
            )}
        </>
    );
};

export default AddSubstitute;
